import { useEffect, useRef } from 'react';
import { UsersIcon } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/EmptyState';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { RetryButton } from '@/shared/components/RetryButton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { useAccessRequests } from '@/features/workspaces';

import { useWorkspaceMembers } from '../hooks/useWorkspaceMembers';
import type { SettingsSection } from '../settingsSearch';
import { AccessRequestsSection } from './AccessRequestsSection';
import { AddMemberPicker } from './AddMemberPicker';
import { MembersSection } from './MembersSection';

export interface PeopleTabProps {
  workspaceId: string;
  workspaceName: string;
  /** Owners and org admins answer requests and change the list. */
  canManage: boolean;
  /** The section a link asked for, scrolled to once it has rendered (spec 193 US6). */
  section?: SettingsSection;
}

/** Each section's heading, which the scroll lands on. */
const SECTION_HEADING: Record<SettingsSection, string> = {
  requests: 'access-requests-heading',
  members: 'members-heading',
};

/**
 * Scroll to `section` once, when `ready` says its content is on the page (spec
 * 193 US6). The router's own hash scroll ran on arrival, before the members and
 * requests were read, and so landed nowhere.
 */
function useScrollToSection(
  section: SettingsSection | undefined,
  ready: boolean,
) {
  const done = useRef<SettingsSection | null>(null);
  useEffect(() => {
    if (section === undefined || !ready || done.current === section) return;
    const heading = document.getElementById(SECTION_HEADING[section]);
    if (heading === null) return;
    done.current = section;
    heading.scrollIntoView({ block: 'start' });
  }, [section, ready]);
}

/**
 * Settings → People (spec 194 US3): who waits to join, then who is in.
 *
 * It reads its own data rather than taking it from the page, so the tab owns its
 * loading and its failure (US5) and another tab never waits on it. The page reads
 * the same members for its band and for General's counts; the cache key is the
 * same, so that is one request.
 *
 * The requests banner sits above the members, as the section did before tabs
 * (spec 190): an answer there is a row that arrives in the list below it.
 * Add member heads the tab, above both (US3.4): it is the tab's own action,
 * and a long requests banner must not push it out of sight.
 */
export function PeopleTab({
  workspaceId,
  workspaceName,
  canManage,
  section,
}: PeopleTabProps) {
  const members = useWorkspaceMembers(workspaceId);
  const showPlaceholder = usePendingDisplay(members.isPending);
  // The same read as the requests section's, so it costs nothing twice.
  const requests = useAccessRequests(workspaceId, canManage);

  // A request already answered leaves no Requests section: land on Members,
  // where the person who was added now is.
  const target: SettingsSection | undefined =
    section === 'requests' && !requests.isPending && requests.items.length === 0
      ? 'members'
      : section;
  useScrollToSection(
    target,
    target === 'requests'
      ? requests.items.length > 0
      : target === 'members'
        ? members.data !== undefined
        : false,
  );

  return (
    <div className="flex flex-col gap-4">
      {canManage && members.data ? (
        // A phone gives the tab's main action the full width (spec 276).
        <div className="flex justify-end max-md:[&>*]:w-full">
          <AddMemberPicker
            workspaceId={workspaceId}
            memberIds={members.data.members.map((member) => member.user.id)}
          />
        </div>
      ) : null}
      <AccessRequestsSection workspaceId={workspaceId} canManage={canManage} />
      {members.data ? (
        <MembersSection
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          members={members.data.members}
          canManage={canManage}
        />
      ) : members.isError ? (
        <EmptyState
          icon={UsersIcon}
          description="The members could not be read just now. This is not an empty workspace — the list could not be reached."
          action={<RetryButton onRetry={() => void members.refetch()} />}
        />
      ) : showPlaceholder ? (
        // Shaped like the list it stands in for: a toolbar, then rows.
        <PendingRegion label="Loading members" className="gap-3">
          <Skeleton className="h-4 w-28 rounded-sm" />
          <Skeleton className="h-9 w-full rounded-md" />
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex items-center gap-3 py-1.5">
              <Skeleton className="size-8 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-40 rounded-sm" />
                <Skeleton className="h-3 w-56 rounded-sm" />
              </div>
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          ))}
        </PendingRegion>
      ) : null}
    </div>
  );
}
